import axios from 'axios';
import posthog from 'posthog-js';

// Free-account daily cap. The API (ProductController::FreeDailyCapReached) answers HTTP 200 with
// {error: true, errorCode: "daily_limit_reached", error_details: {limit: N}}. It is an expected
// business outcome, not a failure, so it gets its own friendly dialog (components/DailyLimitDialog.vue)
// instead of the generic "an error has occurred" one.
export const DAILY_LIMIT_CODE = 'daily_limit_reached';

// Fallback N when the API response carries no limit: profile/report creation is 50 per type.
// A list endpoint can be hit by either the 25-lists or the 300-previews cap, so there is no safe
// number for it -- the dialog then uses its number-less body.
function fallbackDailyLimit(url) {
  return /\/user\/(create\/|search_profiles\/|search_reports\/)/.test(url || '') ? 50 : null;
}

const state = {
  errorBit: false,
  errorInfo: "",
  dailyLimitOpen: false,
  dailyLimit: null,
};

const mutations = {
  // While the daily-limit dialog is open, ignore attempts to raise the generic error dialog. Many
  // callers' own .catch() commit SET_ERROR_BIT/SET_ERROR_INFO unconditionally after the interceptor
  // rejects, which would stack an "An error has occurred" popup (with a stale stack trace) on top of
  // the friendly one. Closing (false / empty) is never blocked. Guarding here keeps ~430 call sites
  // untouched.
  SET_ERROR_BIT: (state, errorBit) => {
    if (errorBit && state.dailyLimitOpen) return;
    state.errorBit = errorBit;
  },
  SET_ERROR_INFO: (state, errorInfo) => {
    if (errorInfo && state.dailyLimitOpen) return;
    state.errorInfo = errorInfo;
  },
  SHOW_DAILY_LIMIT: (state, limit) => {
    state.dailyLimit = limit;
    state.dailyLimitOpen = true;
  },
  SET_DAILY_LIMIT_OPEN: (state, open) => {
    state.dailyLimitOpen = open;
  },
};

const getters = {
  errorBit: (state) => state.errorBit,
  errorInfo: (state) => state.errorInfo,
  dailyLimitOpen: (state) => state.dailyLimitOpen,
  dailyLimit: (state) => state.dailyLimit,
};

const errorHandler = {
  state,
  getters,
  mutations,

  // Log error details with stack trace and performance stats
  logError(type, error) {
    let performance = window.performance;

    /*console.log(`Error Type: ${type}`);
    console.log('Error:', error);
    console.log('Stack Trace:', error.stack);
    console.log('Error payload:', error.requestPayload);
    //console.log('Memory Usage:', `${(performance.memory.usedJSHeapSize / 1048576).toFixed(2)} MB / ${(performance.memory.jsHeapSizeLimit / 1048576).toFixed(2)} MB`);
    console.log('Client Info:', {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language
    });*/

    //state.errorBit = true;
    //state.errorInfo = "Error details...";

    /*throw new Error(
        "<br>" +
        "Server responded with error = True <br> errorCode: "
    );*/
  },

  install(Vue, store) {
    // Add axios to Vue prototype
    //console.log("Installing custom error logger...");
    // Add axios request interceptor
    axios.interceptors.request.use((config) => {
      // Attach request payload to the config
      config.metadata = { startTime: new Date(), payload: config.data }; // Logging start time, and the initial request data.
      return config;
    });

    // Add axios response interceptor
    axios.interceptors.response.use((response) => {
      // Check for "error" flag in the response data

      const endTime = new Date();
      const duration = endTime - response.config.metadata.startTime;
      // Attach request payload to the response
      //response.requestPayload = response.config.metadata.payload;

      if (response.data && response.data.error) {
        const error = new Error(response.data.errorCode || 'An error occurred');
        //error.requestPayload = response.requestPayload;
        error.duration = duration;
        // Tag the rejection so callers can tell a soft API error apart from a real
        // exception. err.stack is not a reliable carrier: it omits the message entirely
        // in Firefox/Safari, so any caller .catch() that commits err.stack destroys the
        // errorCode the error dialogs branch on (see SET_ERROR_INFO below and the guard
        // in company-list-module.js).
        error.errorCode = response.data.errorCode || 'unknown';
        error.softApiError = true;

        // Daily cap: show the friendly dialog and stop. Skips the generic error dialog (SET_ERROR_BIT),
        // which would otherwise stack a "we have been notified" popup on top. Still rejects, so every
        // caller's pending state is released through its own catch/finally.
        if (error.errorCode === DAILY_LIMIT_CODE) {
          error.dailyLimit = true;
          const details = response.data.error_details;
          const apiLimit = details ? parseInt(details.limit, 10) : NaN;
          // Several calls can be refused at once (e.g. count + max-count): open the dialog and report
          // the event only for the first while it is already showing.
          if (store && !store.getters.dailyLimitOpen) {
            store.commit(
              'SHOW_DAILY_LIMIT',
              apiLimit > 0 ? apiLimit : fallbackDailyLimit(response.config.url)
            );
            posthog.capture('daily_limit_reached', {
              endpoint: response.config.url,
              limit: apiLimit > 0 ? apiLimit : null,
            });
          }
          return Promise.reject(error);
        }

        this.logError('HTTP Error', error);
        // Feed soft API errors into PostHog error tracking (same sink as the Vue
        // errorHandler in main.js) -- these are business-level failures the transport
        // layer can't see (HTTP 200 with error:true in the body), and before this they
        // were invisible to any tooling. posthog-js queues safely if init hasn't run.
        posthog.captureException(error, {
          soft_api_error: true,
          errorCode: response.data.errorCode || 'unknown',
          endpoint: response.config.url,
          duration_ms: duration,
        });
        // Rejecting here means this response never reaches a caller's own .then()
        // branch, only .catch()/.finally(). Most call sites only have .then()/.finally()
        // (no .catch()), so without this fallback, soft "error: true" API responses were
        // silently swallowed with no user-facing message at all -- surface it globally
        // via the error store so <error-handler /> can show something regardless of
        // whether the calling code handles the rejection itself.
        if (store) {
          store.commit('SET_ERROR_BIT', true);
          store.commit(
            'SET_ERROR_INFO',
            '<br>Server responded with error = True <br> errorCode: ' +
              (response.data.errorCode || 'unknown') +
              '<br> ' + response.config.url + ' <br>'
          );
        }
        return Promise.reject(error);
      }

      return response;
    }, (error) => {
      if (error.config) {
        error.requestPayload = error.config;
      }
      this.logError('HTTP Error', error);
      // Transport-level failures (timeouts, 5xx, DNS) into PostHog too.
      posthog.captureException(error, {
        transport_error: true,
        endpoint: error.config ? error.config.url : 'unknown',
        status: error.response ? error.response.status : null,
      });
      return Promise.reject(error);
    });

    // Add Vue error handler
    Vue.config.errorHandler = (error, vm, info) => {
      //this.logError('Vue Error', error);
      //console.log('Vue error info:', info);
    };

    // Add window error listener
    window.addEventListener('error', (event) => {
      //this.logError('Global Error', event.error);
    });
  }
};

export default errorHandler;
